import type { StatusType } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { context as otelContext } from '@opentelemetry/api';
import type { LoggerOptions } from '@opentelemetry/api-logs';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import type { Analytics } from '@segment/analytics-next';
import { AnalyticsBrowser } from '@segment/analytics-next';

import {
    ATTRIBUTE_ACTIVITY_TYPE,
    ATTRIBUTE_EVENT_NAME,
    PRODUCT_LOGGER_NAME,
} from './metrics.constants';
import type {
    ExtendedProperties,
    MetricsConfiguration,
    UserAction,
    UserContext,
} from './metrics.types';
import { ActivityType } from './metrics.types';
import { getDataDogIdentifiers } from './trace';

declare global {
    interface Window {
        segmentAnalytics?: Analytics;
        datadog?: boolean;
    }
}
function matchesAnySaasHost(message: string): boolean {
    // Regex for extracting URLs from the string
    const urlRegex = /https?:\/\/[^\s"'<>]+/g;
    const allowedHosts = [
        'api.segment.io',
        'app.launchdarkly.com',
        'events.launchdarkly.com',
        'otel.elastic.seequent.com',
        'www.google-analytics.com',
    ];
    const matches = message.match(urlRegex);
    if (matches) {
        for (const foundUrl of matches) {
            try {
                const parsed = new URL(foundUrl);
                if (allowedHosts.includes(parsed.host)) {
                    return true;
                }
            } catch {
                // Ignore invalid URLs
            }
        }
    }
    return false;
}

function shouldLogError(event: any) {
    // Ignore errors related to CEFSharp: https://github.com/DataDog/browser-sdk/issues/2715
    if (event?.message?.includes('Object Not Found Matching Id')) {
        return false;
    }

    // Discards error messages from noisy integrations that have intermittent request failures
    if (
        (event?.error &&
            event?.message &&
            (matchesAnySaasHost(event.message) ||
                event.message?.includes('ResizeObserver loop completed') ||
                event.message?.includes('access_token='))) ||
        // Azure Blob Storage logs a 404 RestError when we're checking for the existence of a blob and don't find anything.
        event.message?.includes('RestError: The specified blob does not exist.')
    ) {
        return false;
    }
    return true;
}

export const initializeMetrics = async (config: MetricsConfiguration): Promise<void> => {
    const { datadogConfig, env, segmentWriteKey } = config;

    if (segmentWriteKey && !window.segmentAnalytics) {
        try {
            const [analytics] = await AnalyticsBrowser.load({ writeKey: segmentWriteKey });
            window.segmentAnalytics = analytics;
        } catch {
            // The login library creates race conditions spamming Sentry since this request throws error
        }
    }

    if (datadogConfig && !window.datadog) {
        const enableExperimentalFeatures: string[] = [];

        if (datadogConfig.sendFeatureFlags) {
            enableExperimentalFeatures.push('feature_flags');
        }

        const {
            appId,
            beforeSend,
            clientToken,
            enableSessionReplays,
            forwardConsoleLogs,
            forwardErrorsToLogs,
            service,
            version,
        } = datadogConfig;
        datadogRum.init({
            applicationId: appId,
            clientToken,
            site: 'datadoghq.com',
            env: env ?? 'undefined',
            // Specify a version number to identify the deployed version of your application in Datadog
            version,
            // TBD: Appropriate sampling rates for use per env?
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            defaultPrivacyLevel: 'mask',
            actionNameAttribute: 'automation-id',
            telemetrySampleRate: 0,
            useSecureSessionCookie: true,
            compressIntakeRequests: true,
            beforeSend,
            enableExperimentalFeatures,
        });

        datadogLogs.init({
            clientToken,
            site: 'datadoghq.com',
            env: config.env || 'undefined',
            forwardErrorsToLogs: forwardErrorsToLogs ?? true,
            forwardConsoleLogs: forwardConsoleLogs ?? ['error'],
            sessionSampleRate: 100,
            beforeSend: shouldLogError,
            service,
            version,
        });

        // We want to avoid potentially sending confidential user data to third-party applications.
        // Some applications have been approved for production access.
        if (config.env === 'development' || enableSessionReplays) {
            datadogRum.startSessionReplayRecording();
        }
        window.datadog = true;
    }
};

/**
 * Configures future pageviews, events, and errors to be tagged with end-user details
 * @param userContext The end-user details to tag future events with (all optional)
 * @param featureFlags An optional object containing the end-user's feature flag values. You must set `sendFeatureFlags` to `true` in the datadogConfig for the feature flags to be sent to Datadog.
 */
export const setUserContext = async (
    userContext: UserContext,
    featureFlags?: Record<string, boolean>,
): Promise<void> => {
    let { details } = userContext;

    if (window.segmentAnalytics && userContext.userId) {
        if (userContext.orgId && details?.orgId === undefined) {
            details = { ...details, orgId: userContext.orgId };
        }

        await window.segmentAnalytics.identify(userContext.userId, details);
    }

    if (window.datadog && userContext.userId) {
        datadogRum.setUser({ id: userContext.userId, orgId: userContext.orgId, ...details });

        if (featureFlags) {
            Object.entries(featureFlags).forEach(([key, value]) => {
                datadogRum.addFeatureFlagEvaluation(key, value);
            });
        }
    }
};

/**
 * Finish tracking end-user actions when they elect to logout.
 */
export const clearUserContext = () => {
    if (window.datadog) {
        const hasUser = datadogRum.getUser();
        if (Object.keys(hasUser).length) {
            datadogRum.stopSession();
            datadogRum.clearUser();
        }
    }
};

/**
 * Track end-user actions that are not already captured as simple page views or dependency calls.
 * An example might be 'User Reset Password' or 'Workflow x started'
 *
 * @param action One of the pre-set user-actions, or a custom one.
 * @param details Optional details that will be stored with the event as additional properties
 */
export const trackUserAction = async (
    action: UserAction | string,
    details?: ExtendedProperties,
) => {
    if (window.segmentAnalytics) {
        await window.segmentAnalytics.track(action, details);
    }

    if (window.datadog) {
        datadogRum.addAction(`${action}`, details);
    }

    if (window.otelTracer) {
        const logger = logs.getLogger('userAction');
        const attributes = {
            ...details,
            ...getDataDogIdentifiers(),
        };
        const body = {
            name: PRODUCT_LOGGER_NAME,
            [ATTRIBUTE_EVENT_NAME]: action,
        };

        logger.emit({
            severityNumber: SeverityNumber.INFO,
            severityText: 'info',
            body,
            attributes,
            context: otelContext.active(),
        });
    }
};

export const trackPage = async () => {
    if (window.segmentAnalytics) {
        await window.segmentAnalytics.page();
    }
};

/**
 * Track a non-fatal error or fault within the application.
 * For critical errors, and errors that stops users from being able to complete tasks, consider using the error-logging library
 * @param error The error or error-message to track
 * @param message An optional message describing the situation leading to the error
 * @param details Optional details that will be stored with the error as additional properties
 */
export const trackError = (
    error: Error | string,
    message?: string,
    details?: ExtendedProperties,
    severityNumber?: number,
) => {
    if (window.datadog) {
        const context = { message, details };
        datadogRum.addError(error, context);
    }
    if (window.otelTracer) {
        const logger = logs.getLogger('error');
        const attributes = { ...details };
        if (typeof error === 'object') {
            Object.assign(attributes, error);
        }
        const body = { [ATTRIBUTE_EVENT_NAME]: typeof error === 'string' ? error : message };
        if (!message) {
            body[ATTRIBUTE_EVENT_NAME] = (error as any)?.message ?? 'Unknown error';
        }
        logger.emit({
            severityNumber: severityNumber ?? SeverityNumber.ERROR,
            severityText: 'error',
            body,
            attributes,
            context: otelContext.active(),
        });
    }
};

export enum LogLevel {
    debug = 'debug',
    info = 'info',
    warn = 'warn',
    error = 'error',
}

/**
 * The inferMetricActivity function is used to infer the activity type from the message parameter of the customLog function.
 * The activity type is used to categorize the log in the ELK stack.
 * @param actionName the `message` parameter of the customLog function used to infer the activity type
 * @returns {ActivityType} the inferred activity type, or ActivityType.Unknown if the activity type cannot be inferred
 */
export function inferMetricActivity(actionName: string) {
    if (actionName.startsWith('create')) {
        return ActivityType.Create;
    }
    if (actionName.startsWith('update')) {
        return ActivityType.Update;
    }
    if (actionName.startsWith('upload') || actionName.startsWith('import')) {
        return ActivityType.Import;
    }
    if (actionName.startsWith('download') || actionName.startsWith('export')) {
        return ActivityType.Export;
    }
    if (actionName.startsWith('restore')) {
        return ActivityType.Restore;
    }
    if (actionName.startsWith('softDelete')) {
        return ActivityType.SoftDelete;
    }
    if (actionName.startsWith('delete')) {
        return ActivityType.Delete;
    }
    if (actionName.includes('load') || actionName.includes('view')) {
        return ActivityType.View;
    }
    return ActivityType.Unknown;
}

/**
 * The customLog is a wrapper around the datadogLogs & otelTracer loggers. The OTel logs are demarked by the PRODUCT_LOGGER_NAME,
 * such that the logs can be easily filtered in the ELK stack. Furthermore, the customLog function will infer the activity type from the
 * message, and add it as an attribute to the log, as well as, any DataDog identifiers.
 * @param {string} message should start with an actionType keyword to properly categorize the log in the ELK stack
 * Quirky blockmodel usage with output of JSON.stringify({metric: string, value?: boolean|number|string, tags: any})
 * @param {any} context
 * @param {LogLevel} level
 * @param {Error} error
 * @param {number} severityNumber The severityNumber is an optional parameter that can be used to override the default base-level.
 */
export const customLog = (
    message: string,
    context?: object,
    level: LogLevel = LogLevel.info,
    error?: Error,
    severityNumber?: number,
) => {
    if (window.datadog) {
        datadogLogs.logger.log(message, context, level as StatusType, error);
    }
    if (window.otelTracer) {
        const severityText = level.toString();
        const logger = logs.getLogger(PRODUCT_LOGGER_NAME);
        const actionType = inferMetricActivity(message);
        const attributes = {
            ...context,
            ...getDataDogIdentifiers(),
            [ATTRIBUTE_ACTIVITY_TYPE]: actionType,
        };
        if (error) {
            Object.assign(attributes, error);
        }

        // FUTURE: ELK to add support for structured logging?
        let msgObject: any = {};
        try {
            msgObject = JSON.parse(message);
        } catch {
            // do nothing
            msgObject = { metric: message };
        } finally {
            if (!Object.getOwnPropertyNames(msgObject).includes('metric')) {
                msgObject.metric = 'unknown_event';
            }

            const { metric: _, ...remaining } = msgObject;
            Object.assign(attributes, remaining);
        }

        const body = {
            name: PRODUCT_LOGGER_NAME,
            [ATTRIBUTE_EVENT_NAME]: msgObject.metric ?? message,
        };
        logger.emit({
            severityNumber: severityNumber ?? mapLogLevelToSeverityNumber(level),
            severityText,
            body,
            attributes,
            context: otelContext.active(),
        });
    }
};

/**
 * Returns a URL to the Datadog Session Replay for the current user session, if available. This can be used to link from a
 * metrics event to the corresponding session replay in Datadog, to provide additional context on the user actions leading
 * up to the event.
 *
 * @returns {string | undefined} URL to the Datadog Session Replay, or undefined if not available
 */
export function getRumRecordingUrl(): string | undefined {
    if (!window.datadog) {
        return undefined;
    }

    try {
        return datadogRum.getSessionReplayLink();
    } catch {
        return undefined;
    }
}

/**
 * Returns an OpenTelemetry logger from the global logger provider. If no options are provided, the logger will be created
 * with default options, which adds **attributes** of the **getDataDogIdentifiers**. For logs that are Product metrics,
 * the **log** needs to denote certain properties to be parsed for exporting:
 *  - _body_ will need to contain: `name: PRODUCT_LOGGER_NAME`,
 *  - _attributes_ ought to contain **ATTRIBUTE_EVENT_NAME** and **ATTRIBUTE_ACTIVITY_TYPE** with values.
 *
 * @param loggerName
 * @param version optional version string that will default to the `openTelemetryConfig.version` if not provided
 * @param options LoggerOptions that can initialize the logger with custom options
 * @returns Logger
 */
export function getOtelLogger(loggerName: string, version?: string, options?: LoggerOptions) {
    if (window.otelTracer) {
        const dataDogIds = window?.datadog ? getDataDogIdentifiers() : undefined;
        // Call the original getLogger method with all parameters
        const mergedOptions: LoggerOptions = {
            ...options,
            attributes: {
                ...dataDogIds,
                ...(options?.attributes || {}),
            },
        };
        return logs.getLogger(loggerName, version, mergedOptions);
    }

    return undefined;
}

function mapLogLevelToSeverityNumber(level: LogLevel): number {
    switch (level) {
        case LogLevel.debug:
            return SeverityNumber.DEBUG;
        case LogLevel.warn:
            return SeverityNumber.WARN;
        case LogLevel.error:
            return SeverityNumber.ERROR;
        case LogLevel.info:
        default:
            return SeverityNumber.INFO;
    }
}
